import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
} from 'axios';
import { API_PUBLIC_PREFIX, API_SECURED_PREFIX, env } from '@/config/env';
import { tokenStore, workspaceStore } from '@/auth/stores';

/**
 * Shape of the backend's ApiErrorResponse (see its global exception filter).
 */
interface ApiErrorBody {
  status: number;
  timestamp: string;
  message: string;
  errors: string[];
  path: string;
}

/**
 * A failure that already carries a message worth showing the user.
 *
 * The backend returns readable messages ("Instagram requires an image…"), so
 * the UI surfaces them verbatim rather than inventing its own copy.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number | undefined,
    readonly details: string[] = []
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** 401 — the session is gone. The only case that should sign someone out. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  /**
   * 403 — signed in, but not allowed to do this.
   *
   * Kept apart from 401 deliberately. With workspace roles, 403 is an ordinary
   * outcome: a member opening an admin-only screen, or addressing a workspace
   * they have left. Treating it as a session failure would sign people out
   * mid-session for doing something perfectly normal, which is exactly the
   * "it randomly logs me out" bug this split prevents.
   */
  get isForbidden(): boolean {
    return this.status === 403;
  }

  /**
   * 402 — there is a plan boundary in the way.
   *
   * Two different situations share it, and both are answered the same way by
   * the UI: the workspace has spent an allowance, or it has no active plan at
   * all. Either way the fix is on the billing screen, not in a retry, so this
   * is what switches an error banner from "Try again" to "Upgrade".
   *
   * Deliberately not folded into `isForbidden`. A 403 means someone else's
   * permission is missing and no amount of paying will help; a 402 means the
   * opposite.
   */
  get isPaymentRequired(): boolean {
    return this.status === 402;
  }

  /** Either — used to decide "don't retry this query". */
  get isAuthError(): boolean {
    return this.isUnauthenticated || this.isForbidden;
  }
}

function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;

  const axiosError = error as AxiosError<ApiErrorBody>;
  const status = axiosError.response?.status;
  const body = axiosError.response?.data;

  if (status === 401) {
    return new ApiError(
      body?.message || 'Your session has ended. Please sign in again.',
      status
    );
  }

  if (status === 403) {
    return new ApiError(
      body?.message || 'You do not have permission to do that.',
      status
    );
  }

  if (status === 402) {
    // The backend's message names the metric, the number and when it resets —
    // or says the plan has lapsed and that nothing was lost. Both are better
    // than anything invented here, so the fallback is only for a 402 with no
    // body at all.
    return new ApiError(
      body?.message || 'This needs a plan with more room.',
      status,
      body?.errors ?? []
    );
  }

  if (!axiosError.response) {
    return new ApiError(
      `Cannot reach the API at ${env.apiBaseUrl || '(unset)'}. Is the backend running?`,
      undefined
    );
  }

  const details = Array.isArray(body?.errors) ? body.errors : [];
  return new ApiError(
    body?.message || axiosError.message || 'Request failed',
    status,
    details
  );
}

const instance: AxiosInstance = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: 60_000,
  headers: { Accept: 'application/json' },
});

instance.interceptors.request.use((config) => {
  const token = tokenStore.get();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Which workspace this request is for. The backend re-checks membership
  // against it on every call, so it is a routing hint rather than a claim of
  // access — sending the wrong one gets a 403, not someone else's data.
  const workspaceId = workspaceStore.get();
  if (workspaceId) {
    config.headers['X-Workspace-Id'] = workspaceId;
  }

  return config;
});

function isPublicPath(url: string | undefined): boolean {
  return Boolean(url && url.startsWith(`/${API_PUBLIC_PREFIX}`));
}

instance.interceptors.response.use(
  (response) => response,
  (error) => {
    const apiError = toApiError(error);
    const requestUrl = (error as AxiosError)?.config?.url;

    // Clearing the token is what signs the user out: `AuthProvider` subscribes
    // to the store and `RequireAuth` redirects when it empties.
    //
    // Two things it deliberately does NOT do. It does not clear on 403 — see
    // ApiError.isForbidden. And it does not navigate: doing so here would lose
    // the page the user was heading for, force a full reload, and can fire
    // mid-transition. Letting the guard redirect keeps the destination.
    if (apiError.isUnauthenticated && !isPublicPath(requestUrl)) {
      tokenStore.clear();
    }

    return Promise.reject(apiError);
  }
);

function withPrefix(prefix: string, path: string): string {
  return `/${prefix}${path.startsWith('/') ? path : `/${path}`}`;
}

function makeApi(prefix: string) {
  return {
    async get<T>(path: string, config?: AxiosRequestConfig): Promise<T> {
      const { data } = await instance.get<T>(withPrefix(prefix, path), config);
      return data;
    },

    async post<T>(
      path: string,
      body?: unknown,
      config?: AxiosRequestConfig
    ): Promise<T> {
      const { data } = await instance.post<T>(
        withPrefix(prefix, path),
        body,
        config
      );
      return data;
    },

    async put<T>(path: string, body?: unknown): Promise<T> {
      const { data } = await instance.put<T>(withPrefix(prefix, path), body);
      return data;
    },

    async patch<T>(path: string, body?: unknown): Promise<T> {
      const { data } = await instance.patch<T>(withPrefix(prefix, path), body);
      return data;
    },

    async delete<T = void>(path: string): Promise<T> {
      const { data } = await instance.delete<T>(withPrefix(prefix, path));
      return data;
    },
  };
}

/** Secured endpoints — every existing call site uses this. */
export const api = makeApi(API_SECURED_PREFIX);

/** Login, register and the password-reset pair, which run without a token. */
export const publicApi = makeApi(API_PUBLIC_PREFIX);

export { instance as axiosInstance };
